import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { proposalTemplatesService } from "../services/api/proposalTemplatesService";
import "./ReportModals.css";
import "./contacts/AddContactModal.css";

const DAILY = "daily-report";

/** Writes the chosen report into URL params (`view`, and `template` for saved reports). */
export function applyReportSelection(params, selection) {
  if (selection?.templateId) {
    params.set("view", "report");
    params.set("template", String(selection.templateId));
  } else {
    params.set("view", "daily-report");
    params.delete("template");
  }
  return params;
}

/**
 * Popup that lists the built-in Daily report and every saved report template.
 * The user picks one and presses OK.
 */
export default function ReportPickerModal({ open, onClose, onConfirm, projectName }) {
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState("idle");
  const [choice, setChoice] = useState(DAILY);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setChoice(DAILY);
    setStatus("loading");
    proposalTemplatesService
      .list()
      .then((list) => {
        if (cancelled) return;
        setTemplates(Array.isArray(list) ? list : []);
        setStatus("ready");
      })
      .catch((error) => {
        console.error("Could not load saved reports:", error);
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const confirm = () => {
    onConfirm(choice === DAILY ? { templateId: null } : { templateId: choice });
  };

  const option = (value, title, subtitle) => (
    <label key={value} className={`rpt-choice ${choice === value ? "is-selected" : ""}`}>
      <input
        type="radio"
        name="report-choice"
        value={value}
        checked={choice === value}
        onChange={() => setChoice(value)}
      />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span className="rpt-choice-title">{title}</span>
        {subtitle && <span className="rpt-choice-sub">{subtitle}</span>}
      </span>
    </label>
  );

  return createPortal(
    <div
      className="add-contact-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Select a report"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="add-contact-modal-sheet rpt-sheet-narrow">
        <div className="add-contact-modal-header">
          <div style={{ minWidth: 0 }}>
            <h2 className="add-contact-modal-title">Select a report</h2>
            {projectName && <p className="add-contact-modal-subtitle">{projectName}</p>}
          </div>
          <button type="button" className="add-contact-modal-close" onClick={onClose} aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
            </svg>
          </button>
        </div>

        <div className="add-contact-modal-body">
          {option(DAILY, "Daily report", "Elastizell 250 PSI LWIC")}
          {templates.map((tpl) => option(String(tpl.id), tpl.name, tpl.docType))}
          {status === "loading" && <p className="rpt-note">Loading saved reports...</p>}
          {status === "ready" && templates.length === 0 && (
            <p className="rpt-note">
              No saved reports yet. Create one in Configuration &gt; Reports &amp; Proposal Templates.
            </p>
          )}
          {status === "error" && (
            <p className="rpt-note is-error">Could not load saved reports. You can still open the Daily report.</p>
          )}
        </div>

        <div className="add-contact-modal-footer rpt-footer-row">
          <button type="button" className="add-contact-modal-submit rpt-submit-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="add-contact-modal-submit" onClick={confirm}>
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
